/**
 * ProposalMonthlyPerformance — Monthly performance breakdown with Performance/Savings tabs
 *
 * Wraps the shared MonthlyPerformanceTable component to provide the same
 * Performance and Savings tabs used in the main Site DNA results view.
 * The MonthlyPerformanceTable fetches economic data via monthlyPerformanceService
 * (public endpoint, no auth required).
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { vortxColors } from '@vortx/shared-ui';
import MonthlyPerformanceTable from '../../MonthlyPerformanceTable';

export default function ProposalMonthlyPerformance({ scenario }) {
  if (!scenario) return null;

  const results = scenario.last_run_results || scenario.reopt_results;
  if (!results) return null;

  const latestRun = (() => {
    const runs = scenario.optimization_runs || [];
    const baseRuns = runs.filter(r => r.run_type !== 'variant');
    const lastRunUuid = scenario.last_run_uuid;
    if (lastRunUuid) {
      const match = baseRuns.find(r => r.id === lastRunUuid || r.nrel_run_uuid === lastRunUuid);
      if (match) return match;
    }
    const completed = baseRuns.filter(r => ['completed', 'optimal', 'Optimal Obtained'].includes(r.status));
    if (completed.length > 0) {
      return completed.sort((a, b) => {
        const aTime = a.completed_at ? new Date(a.completed_at) : new Date(0);
        const bTime = b.completed_at ? new Date(b.completed_at) : new Date(0);
        return bTime - aTime;
      })[0];
    }
    return baseRuns[0] || null;
  })();

  const runUuid = latestRun?.id || latestRun?.nrel_run_uuid || results?.run_uuid;
  const tariff = scenario.ui_state?.form?.tariff?.customTariffData
    || scenario.ui_state?.form?.tariff?.selectedTariffData
    || scenario.tariff_data
    || results?.inputs?.ElectricTariff;

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          color: vortxColors.stone,
          fontWeight: 600,
          letterSpacing: '0.1em',
          mb: 0,
          display: 'block',
        }}
      >
        Monthly Performance Analysis
      </Typography>

      <MonthlyPerformanceTable
        runUuid={runUuid}
        results={results}
        tariff={tariff}
        utilityCostsOnly
      />
    </Box>
  );
}
